import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Avatar,
  Paper,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';
import PersonIcon from '@material-ui/icons/Person';
import { useSnackbar } from 'notistack';
import Joi from 'joi';
import { getCurrentUser, getLocationInfoFromUser, uploadMultipart } from '../../common/helper';
import { navigate } from '@reach/router';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import handleApiErrors from '../../common/handleApiErrors';
import { getAPIName } from '../../config';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CurrentUser {
  username: string;
  email: string;
  fullData: any;
  location: string;
}

type Category = 'issue_query' | 'violation' | 'general_query' | '';
type Severity = 'low' | 'normal' | 'high' | 'urgent' | '';

// Keys match backend payload keys directly — no mapping needed
interface FormState {
  sender_email: string;
  sender_name: string;
  category: Category;
  severity: Severity;
  message: string;
  attachment_description: string;
  location: string;
}

// FormErrors keys mirror FormState keys for the validated fields
interface FormErrors {
  sender_email?: string;
  sender_name?: string;
  category?: string;
  severity?: string;
  message?: string;
  attachments?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_FILES = 5;
const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_EXTENSIONS = '.pdf,.png,.jpg,.jpeg,.xlsx';

const CATEGORY_OPTIONS: { value: Category; label: string }[] = [
  { value: 'issue_query', label: 'Issue / Query' },
  { value: 'violation', label: 'Violation Upload' },
  { value: 'general_query', label: 'General Question' },
];

const SEVERITY_OPTIONS: { value: Severity; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const EMPTY_FORM: FormState = {
  sender_email: '',
  sender_name: '',
  category: '',
  severity: '',
  message: '',
  attachment_description: '',
  location: '',
};

// ─── Joi Validation Schema ────────────────────────────────────────────────────
const contactUsValidationSchema = Joi.object({
  sender_email: Joi.string()
    .email({ tlds: { allow: false } })
    .required()
    .messages({
      'string.email': 'Email must be a valid email',
      'any.required': 'Email is required',
      'string.empty': 'Email is required',
    }),
  sender_name: Joi.string().required().messages({
    'any.required': 'Name is required',
    'string.empty': 'Name is required',
  }),
  message: Joi.string().required().messages({
    'any.required': 'Message is required',
    'string.empty': 'Message is required',
  }),
  category: Joi.string()
    .valid('issue_query', 'violation', 'general_query')
    .required()
    .messages({
      'any.only': 'Please select a valid category',
      'any.required': 'Category is required',
      'string.empty': 'Category is required',
    }),
  severity: Joi.string()
    .valid('low', 'normal', 'high', 'urgent')
    .required()
    .messages({
      'any.only': 'Please select a valid severity',
      'any.required': 'Severity is required',
      'string.empty': 'Severity is required',
    }),
  attachment_description: Joi.string().optional().allow(''),
  location: Joi.string().optional().allow(''),
});

// ─── Styles ───────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxWidth: 680,
      margin: '0 auto',
      padding: theme.spacing(4, 3),
      border: '1px solid #00000033',
      borderRadius: 12,
      backgroundColor: theme.palette.background.paper,
      position: 'relative',
    },
    pageTitle: {
      fontWeight: 700,
      marginBottom: theme.spacing(1),
      color: theme.palette.text.primary,
    },
    pageSubtitle: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(4),
    },
    userCard: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      padding: theme.spacing(2, 2.5),
      marginBottom: theme.spacing(4),
      borderRadius: 12,
      background:
        theme.palette.type === 'dark'
          ? 'rgba(255,255,255,0.04)'
          : 'rgba(0,0,0,0.03)',
      border: `1px solid ${theme.palette.divider}`,
    },
    avatar: {
      backgroundColor: theme.palette.primary.main,
      width: 44,
      height: 44,
    },
    userName: {
      fontWeight: 600,
      lineHeight: 1.3,
    },
    userEmail: {
      color: theme.palette.text.secondary,
      fontSize: '0.85rem',
    },
    formSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(3),
    },
    rowGroup: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        gridTemplateColumns: '1fr',
      },
    },
    uploadZone: {
      border: `2px dashed ${theme.palette.divider}`,
      borderRadius: 10,
      padding: theme.spacing(2.5),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(1),
      cursor: 'pointer',
      transition: 'border-color 0.2s, background 0.2s',
      '&:hover': {
        borderColor: theme.palette.primary.main,
        background:
          theme.palette.type === 'dark'
            ? 'rgba(255,255,255,0.03)'
            : 'rgba(0,0,0,0.02)',
      },
    },
    uploadZoneError: {
      borderColor: theme.palette.error.main,
    },
    uploadIcon: {
      color: theme.palette.text.secondary,
      fontSize: 32,
    },
    uploadLabel: {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
      textAlign: 'center',
    },
    uploadHint: {
      color: theme.palette.text.disabled,
      fontSize: '0.75rem',
      textAlign: 'center',
    },
    fileList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      marginTop: theme.spacing(1.5),
    },
    fileChip: {
      maxWidth: 220,
      borderRadius: 8,
    },
    divider: {
      margin: theme.spacing(1, 0),
    },
    submitBtn: {
      marginTop: theme.spacing(1),
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      borderRadius: 10,
      fontWeight: 600,
      fontSize: '0.95rem',
      textTransform: 'none',
    },
    hiddenInput: {
      display: 'none',
    },
    sectionLabel: {
      fontWeight: 600,
      fontSize: '0.8rem',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(-1),
    },
    cancelBtn: {
      cursor: 'pointer',
      position: 'absolute',
      top: 12,
      right: 12,
      padding: theme.spacing(1),
      color: theme.palette.text.secondary,
      '&:hover': {
        color: theme.palette.text.primary,
      },
    },
    ticketsLink: {
      marginLeft: 'auto',
      fontSize: '0.85rem',
      fontWeight: 600,
      textDecoration: 'none',
      color: theme.palette.primary.main,
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  })
);

// ─── Component ────────────────────────────────────────────────────────────────
const ContactUs: React.FC = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});

  const isViolation = form.category === 'violation';

  // ─── Load user on mount ──────────────────────────────────────────────────────
  useEffect(() => {
    const loadUser = async () => {
      try {
        const user = await getCurrentUser();
        if (!user) return;
        const givenName = user?.fullData?.attributes?.given_name;
        const familyName = user?.fullData?.attributes?.family_name;
        const location = await getLocationInfoFromUser(givenName, familyName);
        setCurrentUser({ ...user, location });
        setForm(prev => ({
          ...prev,
          sender_email: user.email || '',
          sender_name: user.username || '',
          location: location || '',
        }));
      } catch {
        enqueueSnackbar('Failed to load user details', { variant: 'error' });
      }
    };
    loadUser();
  }, []);

  // ─── Validation ──────────────────────────────────────────────────────────────
  const validate = (): FormErrors => {
    const errs: FormErrors = {};
    const { error } = contactUsValidationSchema.validate(
      { ...form },
      { abortEarly: false }
    );

    if (error) {
      error.details.forEach((detail: Joi.ValidationErrorItem) => {
        const field = detail.path[0] as keyof FormErrors;
        if (!errs[field]) {
          errs[field] = detail.message.replace(/"/g, '');
        }
      });
    }
    // Violation category: exactly 1 PDF required
    if (isViolation) {
      if (attachments.length !== 1) {
        errs.attachments = 'Exactly 1 PDF attachment is required for Violation Upload.';
      } else if (attachments[0].type !== 'application/pdf') {
        errs.attachments = 'Attachment must be a PDF file.';
      }
    }
    return errs;
  };

  // ─── Field Change ────────────────────────────────────────────────────────────
  const handleChange =
    (field: keyof FormState) =>
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | { value: unknown }
      >
    ) => {
      const value = e.target.value as string;
      setForm(prev => ({ ...prev, [field]: value }));
      // Clear the inline error for this field on change
      setErrors(prev => ({ ...prev, [field]: undefined }));
      // Reset attachments when category changes
      if (field === 'category') {
        setAttachments([]);
        setErrors(prev => ({ ...prev, attachments: undefined }));
      }
    };

  // ─── File Handling ───────────────────────────────────────────────────────────
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!selected.length) return;

    // Size check
    for (const file of selected) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        enqueueSnackbar(`"${file.name}" exceeds the ${MAX_FILE_SIZE_MB}MB limit.`, {
          variant: 'error',
        });
        return;
      }
    }
    // Violation: enforce exactly 1 PDF
    if (isViolation) {
      if (selected.length !== 1 || selected[0].type !== 'application/pdf') {
        enqueueSnackbar('Violation Upload requires exactly 1 PDF file.', {
          variant: 'error',
        });
        return;
      }
      setAttachments(selected);
      setErrors(prev => ({ ...prev, attachments: undefined }));
      return;
    }
    // General: enforce max file count
    const combined = [...attachments, ...selected];
    if (combined.length > MAX_FILES) {
      enqueueSnackbar(`You can upload a maximum of ${MAX_FILES} files.`, {
        variant: 'error',
      });
      return;
    }
    setAttachments(combined);
    setErrors(prev => ({ ...prev, attachments: undefined }));
  };

  const removeFile = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // ─── Submit ──────────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      enqueueSnackbar('Please fix the errors before submitting.', {
        variant: 'warning',
      });
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('sender_email', form.sender_email);
      formData.append('sender_name', form.sender_name);
      formData.append('category', form.category);
      formData.append('severity', form.severity);
      formData.append('message', form.message);
      formData.append('location', form.location);
      if (form.attachment_description.trim()) {
        formData.append('attachment_description', form.attachment_description);
      }
      attachments.forEach(file => formData.append('attachments', file));

      const apiName = getAPIName();
      const data = await uploadMultipart( '/api/v1/support/ticket', formData, 'POST');
      enqueueSnackbar(`Ticket #${data.ticketId} created successfully!`, {
        variant: 'success',
      });
      // Reset form to user defaults
      setForm({
        ...EMPTY_FORM,
        sender_email: currentUser?.email || '',
        sender_name: currentUser?.username || '',
        location: currentUser?.location || '',
      });
      setAttachments([]);
      setErrors({});
    } catch (err) {
      handleApiErrors(err, enqueueSnackbar, 'support ticket');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box className={classes.root}>
      <Typography variant="h5" className={classes.pageTitle}>
        Contact Support
      </Typography>
      <Typography variant="body2" className={classes.pageSubtitle}>
        Fill out the form below and our team will get back to you.
      </Typography>

      <div className={classes.cancelBtn} onClick={() => navigate('/')}>
        <FontAwesomeIcon
          icon={faCircleXmark}
          height={20}
          width={20}
          style={{ height: '100%', width: '100%' }}
        />
      </div>

      {/* User Info Card */}
      {currentUser && (
        <Paper elevation={0} className={classes.userCard}>
          <Avatar className={classes.avatar}>
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography className={classes.userName}>
              {currentUser.username}
            </Typography>
            <Typography className={classes.userEmail}>
              {currentUser.email}
            </Typography>
          </Box>
          <a
            href="https://tollaid.zendesk.com/hc/en-us/requests"
            target="_blank"
            rel="noopener noreferrer"
            className={classes.ticketsLink}
          >
            View All Tickets
          </a>
        </Paper>
      )}

      <Box className={classes.formSection}>
        {/* Category + Severity row */}
        <Box className={classes.rowGroup}>
          <FormControl
            variant="outlined"
            size="small"
            required
            error={!!errors.category}
            fullWidth
          >
            <InputLabel>Category</InputLabel>
            <Select
              value={form.category}
              onChange={handleChange('category') as any}
              label="Category"
            >
              <MenuItem value="">
                <em>Select category</em>
              </MenuItem>
              {CATEGORY_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            {errors.category && (
              <FormHelperText>{errors.category}</FormHelperText>
            )}
          </FormControl>

          <FormControl
            variant="outlined"
            size="small"
            required
            error={!!errors.severity}
            fullWidth
          >
            <InputLabel>Severity</InputLabel>
            <Select
              value={form.severity}
              onChange={handleChange('severity') as any}
              label="Severity"
            >
              <MenuItem value="">
                <em>Select severity</em>
              </MenuItem>
              {SEVERITY_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            {errors.severity && (
              <FormHelperText>{errors.severity}</FormHelperText>
            )}
          </FormControl>
        </Box>

        {/* Message */}
        <TextField
          label="Message"
          variant="outlined"
          fullWidth
          required
          multiline
          rows={5}
          value={form.message}
          onChange={handleChange('message')}
          error={!!errors.message}
          helperText={errors.message}
          placeholder="Describe your issue or question..."
        />

        <Divider className={classes.divider} />

        {/* Attachments */}
        <Box>
          <Typography className={classes.sectionLabel}>
            Attachments
            {isViolation
              ? ' — 1 PDF required'
              : ` — up to ${MAX_FILES} files (optional)`}
          </Typography>

          <Box mt={1.5}>
            <input
              ref={fileInputRef}
              type="file"
              className={classes.hiddenInput}
              id="file-upload-input"
              multiple={!isViolation}
              accept={isViolation ? '.pdf' : ALLOWED_EXTENSIONS}
              onChange={handleFileChange}
            />
            <label htmlFor="file-upload-input">
              <Box
                className={`${classes.uploadZone} ${
                  errors.attachments ? classes.uploadZoneError : ''
                }`}
                component="span"
                display="flex"
              >
                <AttachFileIcon className={classes.uploadIcon} />
                <Typography className={classes.uploadLabel}>
                  {isViolation
                    ? 'Click to upload a PDF file'
                    : 'Click to upload files'}
                </Typography>
                <Typography className={classes.uploadHint}>
                  {isViolation
                    ? 'PDF only'
                    : `PDF, PNG, JPG, JPEG, XLSX · Up to ${MAX_FILES} files`}
                </Typography>
              </Box>
            </label>

            {errors.attachments && (
              <FormHelperText error style={{ marginTop: 6 }}>
                {errors.attachments}
              </FormHelperText>
            )}

            {attachments.length > 0 && (
              <Box className={classes.fileList}>
                {attachments.map((file, i) => (
                  <Chip
                    key={`${file.name}-${i}`}
                    label={file.name}
                    size="small"
                    className={classes.fileChip}
                    onDelete={() => removeFile(i)}
                    deleteIcon={<CloseIcon />}
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* Attachment Description — optional */}
        <TextField
          label="Attachment Description"
          variant="outlined"
          fullWidth
          multiline
          rows={2}
          value={form.attachment_description}
          onChange={handleChange('attachment_description')}
          placeholder="Optional notes about the attached files..."
          helperText="Applies to all attachments"
        />

        {/* Submit */}
        <Button
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          className={classes.submitBtn}
          onClick={handleSubmit}
          disabled={submitting}
          endIcon={
            submitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SendIcon />
            )
          }
        >
          {submitting ? 'Submitting…' : 'Submit Request'}
        </Button>
      </Box>
    </Box>
  );
};

export default ContactUs;